'use client'

import { authenticationService } from '@/domain/Authentication'
import { RegisterDto } from '@/domain/Authentication/dtos/register.dto'
import { SignInDto } from '@/domain/Authentication/dtos/sign-in.dto'
import {
    QueryObserverResult,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { Profiler, createContext, useCallback, useContext } from 'react'
import { Customer } from 'stoux-shared-types/models/customer'

type AccountContext = {
    isRetrievingCustomer: boolean
    customer: Customer | null
    refetchCustomer: () => Promise<
        QueryObserverResult<Customer | null, unknown>
    >
    logout: () => Promise<void>
    login: (dto: SignInDto) => Promise<void>
    register: (dto: RegisterDto) => Promise<void>
    checkSession: () => Promise<boolean | undefined>
}

const AccountContext = createContext<AccountContext | null>(null)

type AccountContextProviderProps = {
    children: React.ReactNode
}

function useCustomer() {
    const queryClient = useQueryClient()
    const query = useQuery({
        queryKey: ['customer', 'me'],

        queryFn: () => authenticationService.getUserLoggedIn(),
        retry: false,               
        refetchOnWindowFocus: false, 
    })

    return {
        ...query,
        customer: query.data ?? null,
        isRetrievingCustomer: query.isLoading,
        remove: () => {
            queryClient.removeQueries({
                queryKey: ['customer', 'me'],
                exact: true,
            })
        },
    }
}

function useDeleteSession() {
    const mutation = useMutation({
        mutationFn: () => authenticationService.deleteSession(),
    })
    return mutation
}

function useSignIn() {
    const mutation = useMutation({
        mutationFn: (dto: SignInDto) => authenticationService.signIn(dto),
    })

    return mutation
}

function useRegister() {
    const mutation = useMutation({
        mutationFn: (dto: RegisterDto) => authenticationService.register(dto),
    })

    return mutation
}

function useAuth() {
    const { refetch } = useCustomer()

    const deleteSession = useDeleteSession()
    const signIn = useSignIn()
    const _register = useRegister()

    const router = useRouter()

    const login = async (dto: SignInDto) => {
        await signIn.mutateAsync(dto)
        await refetch()
        router.push('/account')
    }

    const logout = async () => {
        await deleteSession.mutateAsync()
        await refetch()
        router.push('/')
    }

    const register = async (dto: RegisterDto) => {
        await _register.mutateAsync(dto)
    }

    return {
        login,
        logout,
        register,
    }
}

export function AccountContextProvider({
    children,
}: AccountContextProviderProps) {
    const { customer, isRetrievingCustomer, refetch } = useCustomer()

    const { login, logout, register } = useAuth()

    const router = useRouter()

    const checkSession = useCallback(async () => {
        if (!customer && !isRetrievingCustomer) {
            return router.push('/sign-in')
        }
    }, [customer, isRetrievingCustomer, router])


    const contextValue: AccountContext = {
        isRetrievingCustomer,
        customer,
        refetchCustomer: refetch,
        checkSession,
        login,
        logout,
        register,
    }

    return (
        <AccountContext.Provider value={contextValue}>
            {/* <LoadingOverlay visible={isMutating} loaderProps={{ size: 'xs' }} /> */}
            {children}
        </AccountContext.Provider>
    )
}

export function useAccountContext() {
    const context = useContext(AccountContext)
    if (!context) {
        throw new Error(
            'useAccountContext must be used within AccountContextProvider',
        )
    }

    return context
}

// Error : A component suspended while responding to synchronous input.
// This will cause the UI to be replaced with a loading indicator.
// To fix, updates that suspend should be wrapped with startTransition.
